import { t } from "@web-monorepo/i18n/macro";
import { getImageSrc } from "@web-monorepo/static-assets";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import ToolkitFullscreenAudioPlayer from "components/ToolkitFullscreenAudioPlayer";
import ToolkitGridSplitItem from "components/ToolkitGridSplitItem";
import { useState } from "react";
import { closeWebview } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { MINDFULNESS } from "utils/beyondToolkitTools";
import { useQuotaActivity } from "src/pages/beyondToolkit/useQuotaActivity.vm";
import styles from "./index.module.css";

const useVmDefault = () => useQuotaActivity("bedtimeMeditations");

type MindfulnessProps = {
  di?: { useVm?: typeof useVmDefault };
};

function Mindfulness({ di }: MindfulnessProps) {
  const useVm = di?.useVm ?? useVmDefault;
  const { recordUsage } = useVm();

  const [episodeSelected, setEpisodeSelected] = useState<Episode | null>(null);
  const [quotaReached, setQuotaReached] = useState(false);

  const { title, imgSrc } = MINDFULNESS();

  return (
    <div>
      {episodeSelected == null ? (
        <ToolkitAppScroller title={title} variant="bedtimeMeditations" imgSrc={imgSrc}>
          <div className={styles.grid}>
            {getEpisodes().map((episode, i) => {
              return (
                <ToolkitGridSplitItem
                  key={i}
                  index={i}
                  title={t`Day ${{ number: i + 1 }}`}
                  subTitle={episode.subTitle}
                  imgSrc={episode.imgSrc}
                  onItemClick={() => {
                    logParentToolkitEvent({ eventName: `mindfulness.day${i + 1}.tap` });
                    setEpisodeSelected(episode);
                  }}
                />
              );
            })}
          </div>
        </ToolkitAppScroller>
      ) : (
        <ToolkitFullscreenAudioPlayer
          audioSrc={episodeSelected.audioSrc}
          imgSrc={episodeSelected.imgSrc}
          onClose={() => {
            setEpisodeSelected(null);
            if (quotaReached) {
              closeWebview();
            }
          }}
          sendUsage={async () => {
            const { exhausted } = await recordUsage();
            if (exhausted) setQuotaReached(true);
          }}
        />
      )}
    </div>
  );
}

export default Mindfulness;

type Episode = {
  subTitle: string;
  imgSrc: string;
  audioSrc: string;
};

const getEpisodes = () => {
  return [
    {
      subTitle: t`The bell`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day1.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-1.mp3",
    },
    {
      subTitle: t`Calm & breathing`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day2.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-2.mp3",
    },
    {
      subTitle: t`Notice your focus`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day3.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-3.mp3",
    },
    {
      subTitle: t`Your busy mind`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day4.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-4.mp3",
    },
    {
      subTitle: t`Mindful listening`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day5.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-5.mp3",
    },
    {
      subTitle: t`Five things`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day6.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-6.mp3",
    },
    {
      subTitle: t`What’s the weather?`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day7.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-7.mp3",
    },
    {
      subTitle: t`The color of calm`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day8.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-8.mp3",
    },
    {
      subTitle: t`Body scanning`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day9.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-9.mp3",
    },
    {
      subTitle: t`Your white belt!`,
      imgSrc: getImageSrc("toolkit/public/static/images/mindfulness_day10.png"),
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-10.mp3",
    },
  ];
};
